import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X, Pin, QrCode, ExternalLink, Mail } from "lucide-react";
import { api, resolveImageUrl } from "../lib/api";
import { useDominantColor, desaturate } from "../lib/useDominantColor";
import { useLang } from "../contexts/LanguageContext";
import MasonryFeed from "../components/MasonryFeed";

const FACET_KEYS = ["camera", "lens", "iso", "aperture", "shutter", "focal_length"];
const PARTNER_URL = "https://www.dslradvisor.com/login";
const CONTACT_EMAIL = "galeriasocial01@gmail.com";

export default function Home() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const cameraParam = params.get("camera") || "";
  const facets = useMemo(() => {
    const out = {};
    FACET_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) out[k] = v;
    });
    return out;
  }, [params]);
  const hasFacets = Object.keys(facets).length > 0;
  const [photos, setPhotos] = useState([]);
  const [pick, setPick] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(cameraParam);

  useEffect(() => { setQuery(cameraParam); }, [cameraParam]);

  useEffect(() => {
    setLoading(true);
    const reqs = [api.get("/photos", { params: facets })];
    if (!hasFacets) reqs.push(api.get("/photos/pick"));
    Promise.all(reqs)
      .then((res) => {
        setPhotos(res[0].data);
        setPick(res[1]?.data || null);
      })
      .catch(() => {
        setPhotos([]);
        setPick(null);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    const next = new URLSearchParams(params);
    if (q) next.set("camera", q);
    else next.delete("camera");
    setParams(next);
  };

  const clearSearch = () => {
    setQuery("");
    const next = new URLSearchParams(params);
    next.delete("camera");
    setParams(next);
  };

  const clearFacet = (k) => {
    const next = new URLSearchParams(params);
    next.delete(k);
    setParams(next);
    if (k === "camera") setQuery("");
  };

  const clearAll = () => {
    setQuery("");
    setParams({});
  };

  const pickImageUrl = pick ? resolveImageUrl(pick.thumb_url || pick.image_url) : null;
  const dominant = useDominantColor(pickImageUrl);
  const heroBg = useMemo(() => {
    if (!dominant) return undefined;
    const desat = desaturate(dominant, 0.65); // mostly neutral, hint of color
    return {
      // Radial gradient that originates from behind the featured image (right
      // 70% of the viewport, upper third) and fades into the charcoal base.
      backgroundImage: `radial-gradient(900px 700px at 78% 32%, ${desat.replace("rgb(", "rgba(").replace(")", ",0.45)")} 0%, rgba(54,64,74,0) 65%), linear-gradient(180deg, #36404A 0%, #2e3740 100%)`,
    };
  }, [dominant]);
  const pickShadow = "0 20px 40px rgba(0,0,0,0.4)";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&bgcolor=050505&color=fdfdfd&data=${encodeURIComponent(PARTNER_URL)}`;

  return (
    <div data-testid="home-page">
      {/* HERO — hide on any active search/facet */}
      {!hasFacets && (
        <section
          className="border-b border-zinc-900 relative overflow-hidden"
          style={heroBg}
          data-testid="hero-section"
        >
          <div className="mx-auto max-w-[1600px] px-6 sm:px-10 pt-16 sm:pt-24 pb-14 sm:pb-20 grid gap-10 lg:grid-cols-12 relative">
            <div className="lg:col-span-7 flex flex-col justify-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-6 animate-fade-in-up">
                {t("hero_eyebrow")}
              </div>
              <h1
                className="font-serif font-light text-6xl sm:text-7xl lg:text-8xl xl:text-[8.5rem] tracking-tighter leading-[0.92] text-zinc-50 animate-fade-in-up"
                data-testid="hero-headline"
              >
                {t("tagline")}
              </h1>
              <p className="mt-8 max-w-xl text-lg font-light leading-relaxed text-zinc-300/90 animate-fade-in-up-delay-1">
                {t("lead")}
              </p>
              <div className="mt-10 flex flex-wrap gap-3 animate-fade-in-up-delay-2">
                <a
                  href="#feed"
                  className="group inline-flex items-center bg-sienna/80 text-white px-6 py-3 text-sm font-light tracking-wide border border-white/15 [backdrop-filter:blur(10px)] shadow-[0_10px_30px_-12px_rgba(220,38,38,0.55)] hover:bg-sienna/95 hover:scale-[1.04] hover:shadow-[0_18px_40px_-12px_rgba(220,38,38,0.7)] transition-all duration-300"
                  data-testid="hero-explore-button"
                >
                  {t("explore_feed")}
                </a>
                <Link
                  to="/register"
                  className="inline-flex items-center border border-white/15 bg-white/[0.04] text-zinc-100 px-6 py-3 text-sm font-light tracking-wide [backdrop-filter:blur(10px)] hover:bg-white/[0.08] hover:border-white/30 hover:scale-[1.04] transition-all duration-300"
                  data-testid="hero-join-button"
                >
                  {t("join")}
                </Link>
              </div>

              {/* Partner card — high-visibility outbound link + QR code */}
              <a
                href={PARTNER_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t("partner_eyebrow")} — ${t("partner_title")} (opens in a new tab)`}
                className="mt-10 max-w-md flex items-stretch border border-white/15 bg-black/30 [backdrop-filter:blur(10px)] hover:border-sienna/80 hover:bg-black/40 transition-all duration-300 group animate-fade-in-up-delay-3"
                data-testid="partner-link-card"
              >
                <div className="flex-1 px-5 py-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-sienna/90 flex items-center gap-2">
                    <QrCode size={12} />
                    {t("partner_eyebrow")}
                  </div>
                  <div className="font-serif text-xl text-zinc-50 font-light mt-1 leading-tight">
                    {t("partner_title")}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-2 flex items-center gap-1.5 group-hover:text-zinc-200 transition-colors">
                    dslradvisor.com/login
                    <ExternalLink size={10} />
                  </div>
                </div>
                <div className="w-[88px] flex items-center justify-center bg-[#050505] border-l border-white/10 p-2">
                  <img
                    src={qrUrl}
                    alt={`QR code to ${PARTNER_URL}`}
                    width={72}
                    height={72}
                    loading="lazy"
                    onError={(e) => {
                      // Fallback when the external QR service is unreachable
                      // — render a tiny inline SVG monogram so the card never
                      // shows a broken-image icon.
                      e.currentTarget.outerHTML =
                        '<div class="w-[72px] h-[72px] flex items-center justify-center font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 border border-white/15">SCAN</div>';
                    }}
                    className="w-[72px] h-[72px]"
                    data-testid="partner-qr-code"
                  />
                </div>
              </a>
            </div>
            {pick && (
              <Link
                to={`/photo/${pick.id}`}
                className="lg:col-span-5 block transition-transform duration-500 hover:-translate-y-1 animate-fade-in-up-delay-1"
                data-testid="hero-featured-card"
                style={{
                  filter: `drop-shadow(${pickShadow})`,
                }}
              >
                <div
                  className="relative overflow-hidden aspect-[4/5] bg-zinc-900 border border-white/10"
                  style={{ borderRadius: "4px", boxShadow: pickShadow }}
                >
                  <img
                    src={pickImageUrl}
                    alt={pick.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                  <div
                    className="absolute top-4 left-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-100 bg-zinc-950/70 px-3 py-1.5 border border-white/15 [backdrop-filter:blur(8px)]"
                    style={{ borderRadius: "4px" }}
                    data-testid="hero-pick-badge"
                  >
                    <Pin size={10} /> {t("pick_of_week")}
                  </div>
                </div>
                <div
                  className="flex items-center justify-between px-5 py-4 bg-black/30 border border-t-0 border-white/10 [backdrop-filter:blur(8px)]"
                  style={{ borderRadius: "0 0 4px 4px" }}
                >
                  <div>
                    <div className="font-serif text-xl text-zinc-50 font-light">{pick.title}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-1">
                      {t("by_artist")} · {pick.user_name}
                    </div>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    {pick.appreciations} ♥
                  </div>
                </div>
              </Link>
            )}
          </div>
        </section>
      )}

      {/* WELCOME — editorial intro letter, hidden on any active facet */}
      {!hasFacets && (
        <section
          className="border-b border-zinc-900"
          data-testid="welcome-section"
        >
          <div className="mx-auto max-w-[1100px] px-6 sm:px-10 py-20 sm:py-28 grid gap-12 lg:grid-cols-12">
            <aside className="lg:col-span-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-sienna">
                {t("welcome_eyebrow")}
              </div>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-zinc-50 font-light tracking-tighter leading-[0.95]">
                {t("welcome_title")}
              </h2>
              <div className="mt-6 h-px w-12 bg-sienna" />
            </aside>
            <div className="lg:col-span-8 space-y-6">
              <p className="font-serif text-xl sm:text-2xl font-light leading-relaxed text-zinc-200">
                {t("welcome_p1")}
              </p>
              <p className="text-zinc-300/90 font-light leading-relaxed">
                {t("welcome_p2")}
              </p>
              <p className="text-zinc-300/90 font-light leading-relaxed">
                {t("welcome_p3")}
              </p>
              <div className="pt-4 border-t border-zinc-900 mt-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
                  {t("contact_eyebrow")}
                </div>
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Social Gallery — feedback")}`}
                  className="inline-flex items-center gap-3 text-zinc-100 hover:text-sienna transition-colors group"
                  data-testid="contact-email-link"
                >
                  <span className="w-9 h-9 inline-flex items-center justify-center border border-zinc-800 group-hover:border-sienna transition-colors">
                    <Mail size={14} />
                  </span>
                  <span className="font-mono text-sm tracking-tight underline-offset-4 group-hover:underline">
                    {CONTACT_EMAIL}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FEED */}
      <section id="feed" className="mx-auto max-w-[1600px] px-6 sm:px-10 py-16 sm:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-zinc-900 pb-6 mb-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {hasFacets ? t("filtered_feed") : t("global_feed")}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-zinc-50 font-light mt-2 tracking-tight">
              {cameraParam ? `“${cameraParam}”` : t("explore")}
            </h2>
            {hasFacets && (
              <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="facet-chips">
                {FACET_KEYS.filter((k) => facets[k]).map((k) => (
                  <button
                    key={k}
                    onClick={() => clearFacet(k)}
                    className="inline-flex items-center gap-2 border border-zinc-800 hover:border-sienna hover:text-sienna px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 transition-colors"
                    data-testid={`facet-chip-${k}`}
                  >
                    <span className="text-zinc-500">{t(k)}</span>
                    <span>{facets[k]}</span>
                    <X size={10} />
                  </button>
                ))}
                <button
                  onClick={clearAll}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-200 underline-offset-4 hover:underline ml-1"
                  data-testid="facet-clear-all"
                >
                  {t("clear_all")}
                </button>
              </div>
            )}
          </div>
          <form onSubmit={submitSearch} className="flex items-center gap-2 border border-zinc-800 focus-within:border-zinc-500 transition-colors px-3 py-2 min-w-[260px]" data-testid="camera-search-form">
            <Search size={14} className="text-zinc-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search_placeholder")}
              className="bg-transparent outline-none text-sm font-light text-zinc-100 placeholder:text-zinc-600 w-full"
              data-testid="camera-search-input"
            />
            {cameraParam && (
              <button type="button" onClick={clearSearch} className="text-zinc-500 hover:text-zinc-200" data-testid="camera-search-clear">
                <X size={14} />
              </button>
            )}
          </form>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 w-full sm:w-auto">
            {photos.length} {t("frames")}
          </div>
        </div>
        {loading ? (
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
            {t("loading")}
          </div>
        ) : photos.length === 0 ? (
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500" data-testid="no-results">
            {t("no_results")}
          </div>
        ) : (
          <MasonryFeed photos={photos} />
        )}
      </section>
    </div>
  );
}
